import { zodResolver } from '@hookform/resolvers/zod';
import * as E from '@react-email/components';
import { type ActionFunctionArgs, json } from '@remix-run/node';
import { Form, Link, useFetcher } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import { getValidatedFormData, useRemixForm } from 'remix-hook-form';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { Container } from '#app/components/container.tsx';
import { Button } from '#app/components/ui/button.tsx';
import {
	FormControl,
	FormField,
	FormItem,
	FormMessage,
	FormWrapper,
} from '#app/components/ui/form.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { Input } from '#app/components/ui/input.tsx';
import { Label } from '#app/components/ui/label.tsx';
import { sendEmail } from '#app/utils/email.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';

const UPBEAT_FROM_EMAIL_ADDRESS = 'leads@upbeat.works';
const UPBEAT_TO_EMAIL_ADDRESS = 'hello@upbeat.works';
const budgetOptions = [
	{
		value: 'Needs help figuring out the budget',
		label: 'contact.budget.0',
	},
	{ value: '5k budget', label: 'contact.budget.1' },
	{ value: '10k budget', label: 'contact.budget.2' },
	{ value: '50k budget', label: 'contact.budget.3' },
];

const ContactFormSchema = z.object({
	name: z.string().min(3).max(100),
	email: z
		.string()
		.email()
		.min(3)
		.max(100)
		// users can type the email in any case, but we store it in lowercase
		.transform(value => value.toLowerCase()),
	company: z.string().max(100).optional(),
	phone: z.string().max(20).optional(),
	message: z.string().max(280).optional(),
	budget: z.enum(
		budgetOptions.map(option => option.value) as [string, ...string[]],
	),
});

export const contactFormResolver = zodResolver(ContactFormSchema);

export const isEmpty = function (str: string | null | undefined) {
	return null === str || undefined === str ? true : /^[\s\xa0]*$/.test(str);
};

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.clone().formData();
	checkHoneypot(formData);

	const {
		data,
		errors,
		receivedValues: defaultValues,
	} = await getValidatedFormData(formData, contactFormResolver);

	if (errors) {
		return json({ errors, success: false }, { status: 400 });
	}

	// send the email
	const res = await sendEmail({
		from: UPBEAT_FROM_EMAIL_ADDRESS,
		to: UPBEAT_TO_EMAIL_ADDRESS,
		subject: `New contact form submission from ${data.name}`,
		react: (
			<E.Html lang="en" dir="ltr">
				<E.Container>
					<h1>
						<E.Text>New contact form submission from {data.name}</E.Text>
					</h1>
					<p>
						<E.Text>
							A new contact form submission has been received from {data.name}.
						</E.Text>
						<E.Text>
							They're interested in working with us and are seeking the
							following:
						</E.Text>
						{!isEmpty(data.message) && <E.Text>{data.message}</E.Text>}
						<E.Text>
							They can be reached at {data.email}{' '}
							{!isEmpty(data.phone) && <>or {data.phone}</>}.
						</E.Text>
						{!isEmpty(data.budget) && (
							<E.Text>Their budget is "{data.budget}".</E.Text>
						)}
					</p>
				</E.Container>
			</E.Html>
		),
	});

	if (res.status === 'error') {
		console.error('Error sending email', res.error);
		return json(
			{
				success: false,
				defaultValues,
				errors: {
					root: {
						message:
							'An error occurred while saving your details =(. Please come back later.',
					},
				},
			},
			{ status: 400 },
		);
	}

	return json({ success: true });
}

export default function Contact() {
	const fetcher = useFetcher<typeof action>();
	const { t } = useTranslation();

	const form = useRemixForm<z.infer<typeof ContactFormSchema>>({
		resolver: contactFormResolver,
		fetcher,
	});

	const formSuccess = fetcher.data?.success;

	return (
		<Container className=" min-h-[calc(100vh-100px)] items-center">
			<section>
				<div className="mx-auto mt-24 max-w-7xl sm:mt-32 lg:mt-40">
					<div className="mx-auto max-w-2xl lg:max-w-none">
						<div className="grid grid-cols-1 gap-x-8 gap-y-24 text-primary-darker lg:grid-cols-2">
							<div
								className="lg:order-last"
								style={{ opacity: 1, transform: 'none' }}
							>
								<FormWrapper {...form}>
									<Form
										method="post"
										onSubmit={form.handleSubmit}
										className="space-y-4"
									>
										<HoneypotInputs />
										<h2 className="font-display text-base font-semibold">
											{t('contact.title')}
										</h2>

										<div className="relative isolate mt-6 rounded-2xl bg-white/50">
											<div className="group relative z-0 transition-all focus-within:z-10">
												<FormField
													control={form.control}
													name="name"
													render={({ field }) => (
														<FormItem className="relative">
															<input type={'hidden'} value={field.value} />
															<FormControl>
																<Input
																	{...field}
																	value={field.value ?? ''}
																	id=":S1:"
																	autoComplete="name"
																	type="text"
																	name="name"
																/>
															</FormControl>
															<FormMessage className="absolute right-6 top-0" />
														</FormItem>
													)}
												/>
												<div
													className={'relative'}
													style={{
														height: '0px !important',
														width: '0px !important',
													}}
													data-lastpass-icon-root=""
												></div>
												<Label
													htmlFor=":S1:"
													className="peer-[:not(:placeholder-shown)]: pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold"
												>
													{t('contact.name')}
												</Label>
											</div>
											<div className="group relative z-0 transition-all focus-within:z-10">
												<FormField
													control={form.control}
													name="email"
													render={({ field }) => (
														<FormItem className="relative">
															<input type={'hidden'} value={field.value} />
															<FormControl>
																<Input
																	{...field}
																	value={field.value ?? ''}
																	id=":S2:"
																	autoComplete="email"
																	type="email"
																	name="email"
																/>
															</FormControl>
															<FormMessage className="absolute right-6 top-0" />
														</FormItem>
													)}
												/>
												<Label
													htmlFor=":S2:"
													className="pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-neutral-950"
												>
													{t('contact.email')}
												</Label>
											</div>
											<div className="group relative z-0 transition-all focus-within:z-10">
												<FormField
													control={form.control}
													name="company"
													render={({ field }) => (
														<FormItem className="relative">
															<FormControl>
																<Input
																	{...field}
																	value={field.value ?? ''}
																	id=":S3:"
																	autoComplete="organization"
																	type="text"
																	name="company"
																/>
															</FormControl>
															<FormMessage className="absolute right-6 top-0" />
														</FormItem>
													)}
												/>
												<Label
													htmlFor=":S3:"
													className="peer-[:not(:placeholder-shown)]: pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold"
												>
													{t('contact.company')}
												</Label>
											</div>
											<div className="group relative z-0 transition-all focus-within:z-10">
												<FormField
													control={form.control}
													name="phone"
													render={({ field }) => (
														<FormItem className="relative">
															<FormControl>
																<Input
																	{...field}
																	value={field.value ?? ''}
																	id=":S4:"
																	autoComplete="tel"
																	type="tel"
																	name="phone"
																/>
															</FormControl>
															<FormMessage className="absolute right-6 top-0" />
														</FormItem>
													)}
												/>
												<Label
													htmlFor=":S4:"
													className="peer-[:not(:placeholder-shown)]: pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold"
												>
													{t('contact.phone')}
												</Label>
											</div>
											<div className="group relative z-0 transition-all focus-within:z-10">
												<FormField
													control={form.control}
													name="message"
													render={({ field }) => (
														<FormItem className="relative">
															<input type={'hidden'} value={field.value} />
															<FormControl>
																<Input
																	{...field}
																	value={field.value ?? ''}
																	id=":S5:"
																	type="text"
																	name="message"
																/>
															</FormControl>
															<FormMessage className="absolute right-6 top-0" />
														</FormItem>
													)}
												/>
												<Label
													htmlFor=":S5:"
													className="pointer-events-none absolute left-6 top-1/2 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-neutral-950"
												>
													{t('contact.message')}
												</Label>
											</div>

											<div className="border border-neutral-300 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
												<fieldset>
													<legend className="text-base/6 text-neutral-500">
														{t('contact.budget')}
													</legend>

													<div
														className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2"
														role="radiogroup"
														aria-labelledby="budget"
													>
														{budgetOptions.map(option => (
															<Label
																className="flex gap-x-3"
																key={option.value}
															>
																<FormField
																	control={form.control}
																	name="budget"
																	render={({ field }) => (
																		<FormItem>
																			<FormControl>
																				<input
																					{...field}
																					className="h-6 w-6 flex-none appearance-none rounded-full border border-neutral-950/20 outline-none checked:border-[0.5rem] checked:border-neutral-950 focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
																					type="radio"
																					value={option.value}
																					name="budget"
																				/>
																			</FormControl>
																		</FormItem>
																	)}
																/>
																<span className="text-base/6 ">
																	{t(option.label)}
																</span>
															</Label>
														))}
													</div>
												</fieldset>
											</div>
											{formSuccess && (
												<div className="absolute top-0 z-10 flex h-full w-full items-center justify-center rounded-2xl text-neutral-950 backdrop-blur">
													<div className="flex w-2/3 flex-col items-center justify-center px-6">
														<Icon
															name="envelope-closed"
															className="block h-16 w-16 pb-4 text-primary"
														/>
														<p className="text-center text-xl">
															{t('contact.success')}
														</p>
													</div>
												</div>
											)}
										</div>

										<div>
											<FormField
												name="root"
												render={() => <FormMessage className={'mt-6'} />}
											/>
										</div>
										{!formSuccess && (
											<div className="w-100">
												<Button
													type="submit"
													className={'w-full rounded-2xl'}
													size={'wide'}
												>
													<span className="relative top-px">
														{t('contact.submit')}
													</span>
												</Button>
											</div>
										)}
									</Form>
								</FormWrapper>
							</div>

							<div style={{ opacity: 1, transform: 'none' }}>
								<h2 className="font-display text-base font-semibold ">
									{t('contact.visit')}
								</h2>
								<p className="mt-6 text-base text-neutral-600">
									{t('contact.visitText')}
								</p>
								<ul className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
									<li>
										<address className="text-sm not-italic text-neutral-600">
											<strong className="">Madrid</strong>
											<br />
											Zurbano 45, 1
											<br />
											28010 Madrid, Madrid, Spain
										</address>
									</li>
								</ul>
								<div className="relative mt-16 pt-16 before:absolute before:left-0 before:top-0 before:h-px before:w-6 before:bg-neutral-950 after:absolute after:left-8 after:right-0 after:top-0 after:h-px after:bg-neutral-950/10">
									<h2 className="font-display text-base font-semibold ">
										{t('contact.email-us')}
									</h2>
									<dl className="mt-6 grid grid-cols-1 gap-8 text-sm sm:grid-cols-2">
										<div>
											<dt className="font-semibold ">
												{t('contact.email-address')}
											</dt>
											<dd>
												<Link
													className="hover: text-neutral-600"
													to="mailto:hello@upbeat.works"
												>
													hello@upbeat.works
												</Link>
											</dd>
										</div>
									</dl>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</Container>
	);
}
